<template>
  <div class="item-cell" :class="typeClass" @click="handleClick" :title="itemDetailsTooltip">
    <!-- Item Icon -->
    <img :src="item.icon" alt="Item Icon" class="item-icon"/>

    <!-- Type Indicator -->
    <div class="type-indicator">{{ item.type }}</div>

    <!-- Additional Info Overlay -->
    <div class="overlay" v-if="chargesLeft !== null">
      <div class="charges">
        <span class="charges-label">{{ $t('playerComponents.items.charges') }}</span>
        <span class="charges-value">{{ chargesLeft }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ItemCell",
  props: {
    itemData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    item() {
      return this.itemData.item || {};
    },
    chargesLeft() {
      return this.itemData.charges_left || null;
    },
    typeClass() {
      // Assign color class based on type
      const typeColors = {
        weapon: "type-weapon",
        armor: "type-armor",
        artifact: "type-artifact",
        amulet: "type-amulet",
        material: "type-material",
        quest: "type-quest",
        misc: "type-misc",
        food: "type-food",
        rune: "type-rune",
        default: "type-default",
      };
      return typeColors[this.item.type] || typeColors.default;
    },
    itemDetailsTooltip() {
      return `${this.item.name}\n${this.item.description}\nCharges Left: ${this.chargesLeft}`;
    },
  },
  methods: {
    handleClick() {
      this.$emit("item-clicked", this.itemData.id);
    },
  },
};
</script>

<style scoped>
/* One hue per item type, but carried by a hairline and a label rather than a
   filled pill - a wall of saturated badges fought the tactical HUD behind it. */
.item-cell {
  --tone: #7f929c;

  position: relative;
  display: flex;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 5rem;
  background: rgba(11, 18, 24, 0.72);
  border: 1px solid rgba(0, 255, 255, 0.18);
  border-radius: 0.2rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.item-cell::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 2px;
  background: var(--tone);
  opacity: 0.75;
}

.item-cell:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--tone) 60%, transparent);
  box-shadow: 0 0 0.6rem color-mix(in srgb, var(--tone) 30%, transparent);
}

.item-icon {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.92);
}

.type-indicator {
  position: absolute;
  top: 0.25rem;
  left: 0.3rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.42rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--tone);
  text-shadow: 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 6px rgba(0, 10, 14, 0.9);
}

.type-weapon { --tone: #e88a84; }
.type-armor { --tone: #7fd6a2; }
.type-artifact { --tone: #b79ae8; }
.type-amulet { --tone: #f2c489; }
.type-material { --tone: #9dc98a; }
.type-quest { --tone: #8ab4e8; }
.type-misc { --tone: #9aacb5; }
.type-food { --tone: #e8a97f; }
.type-rune { --tone: #3ee9f0; }
.type-default { --tone: #7f929c; }

.overlay {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0.15rem 0.3rem;
  background: linear-gradient(to top, rgba(5, 9, 12, 0.94), rgba(5, 9, 12, 0.62));
}

.charges {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.25rem;
}

.charges-label {
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.4rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #7f929c;
}

.charges-value {
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 0.6rem;
  font-weight: 600;
  color: #d3e0e6;
}
</style>
